// src/context/ThemeContext.tsx
import { createContext, useContext, useState, useMemo, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider, PaletteMode, CssBaseline } from '@mui/material';

type ThemeContextType = {
  mode: PaletteMode;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext debe usarse dentro de ThemeContextProvider');
  return ctx;
};

export const ThemeContextProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<PaletteMode>(
    (localStorage.getItem('themeMode') as PaletteMode) || 'light'
  );

  const toggleTheme = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  useEffect(() => {
    if (!localStorage.getItem('themeMode')) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#6366f1", // azul personalizado
          },
          secondary: {
            main: "rgb(255, 255, 255)", // azul personalizado BOTON
          },
          text: {
            primary: mode === "light" ? "#1a1a1a" : "#ffffff",
            secondary: mode === "light" ? "#322f3e" : "#fcfcfc",
            disabled: mode === "light" ? "#000000" : "#000000",
          },
          background: {
            default: mode === "dark" ? "#000000" : "#ffffff",
          },

        },
        zIndex: {
          appBar: 1100,
          modal: 1300
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontSize: '2.5rem', fontWeight: 700 },
          h4: { fontSize: '1.5rem', fontWeight: 500 },
          body1: { fontSize: '1rem', lineHeight: 1.6 },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              '*': {
                transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
              },
              body: {
                transition: 'background-color 0.3s ease, color 0.3s ease',
              },
            },
          },
        },

      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
